import _ from 'lodash';
import { useMemo } from 'react';
import { KEY_RECOIL } from '@/shared/config';
import { selectEventChanges, useReportStore } from '@/entities/report/@x/event';
import type { IBeatEventCount, IEventInfo, IRhythmEventCount } from '@/shared/model';

/**
 * Derives the per-event-type review progress shown on the rhythm events tab. Merges the beat and rhythm count maps, marks a type fully reviewed when it has no events, and otherwise adjusts the server-reported reviewed count by every local change whose snake-cased old type matches and whose reviewed flag flipped, expressing the result as a percentage with two decimals.
 *
 * @param holterRhythmEventsCount - Server counts and reviewed totals per rhythm event type.
 * @param holterBeatEventsCount - Server counts and reviewed totals per beat event type.
 * @param eventChanges - Local event edits, scanned for flipped reviewed flags.
 * @returns A map of event type to its review progress percentage and event count.
 */
const computeEventsInfo = (
  holterRhythmEventsCount: IRhythmEventCount,
  holterBeatEventsCount: IBeatEventCount,
  eventChanges: any[],
): IEventInfo => {
  const mergeHolterEventsCount = { ...holterBeatEventsCount, ...holterRhythmEventsCount };
  const reviewProgress: any = {};
  _.forEach(Object.keys(mergeHolterEventsCount), (eventType) => {
    const eventCount =
      mergeHolterEventsCount[eventType as keyof (IBeatEventCount & IRhythmEventCount)]?.count;
    // No event => Mark as Reviewed
    if (eventCount === 0) {
      reviewProgress[eventType] = {
        progress: 100,
        count: eventCount,
      };
    } else {
      // Count review in event changes
      let calculateReviewed =
        mergeHolterEventsCount[eventType as keyof (IBeatEventCount & IRhythmEventCount)]?.reviewed;
      const formatEventType = _.snakeCase(eventType).toUpperCase();
      _.forEach(eventChanges, (x) => {
        if (x.oldType === formatEventType && x.isReviewed !== x.oldReviewed) {
          calculateReviewed += x.oldReviewed ? -1 : 1;
        }
      });
      reviewProgress[eventType] = {
        progress: Number(((calculateReviewed * 100) / eventCount).toFixed(2)),
        count: eventCount,
      };
    }
  });
  return reviewProgress;
};

/**
 * Subscribes the rhythm events tab to its per-event-type review progress. Selects the rhythm and beat event counts and the tab two event changes from the report store and memoises the progress computation against those three inputs.
 *
 * @returns The review progress map keyed by event type.
 */
export const useEventsInfoRhythmEvents = (): IEventInfo => {
  const holterRhythmEventsCount = useReportStore((s) => s.rhythmEventCount);
  const holterBeatEventsCount = useReportStore((s) => s.beatEventCount);
  const eventChanges = useReportStore(selectEventChanges(KEY_RECOIL.RHYTHM_EVENT));
  return useMemo(
    () => computeEventsInfo(holterRhythmEventsCount, holterBeatEventsCount, eventChanges),
    [holterRhythmEventsCount, holterBeatEventsCount, eventChanges],
  );
};

/**
 * Exposes an imperative reader for the rhythm events review progress, for callers outside the render path. The returned callback pulls the rhythm and beat counts plus the tab two event changes straight off the report store snapshot and recomputes the progress on every call, so nothing subscribes.
 *
 * @returns A callback computing the current review progress map.
 */
export const useReadEventsInfoRhythmEvents = () => (): IEventInfo => {
  const { rhythmEventCount, beatEventCount } = useReportStore.getState();
  const eventChanges = selectEventChanges(KEY_RECOIL.RHYTHM_EVENT)(useReportStore.getState());
  return computeEventsInfo(rhythmEventCount, beatEventCount, eventChanges);
};
